// src/layout/Sidebar.jsx

import { Layout, Menu } from "antd";
import { MenuFoldOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { getMenuItems } from "./menuItems";
import ProfileSection from "./ProfileSection";

import logo from "../assets/images/XynexAI_Logo.png";
import small_logo from "../assets/images/Small_XynexAI_Logo.png";
import SubscriptionModal from "./SubscriptionModal";

const { Sider } = Layout;

const Sidebar = ({
  collapsed,
  setCollapsed,
  navigate,
  handleLogout,
  userInfo,
}) => {
  const location = useLocation();
  const [is_subscription_modal_open, setIsSubscriptionModalOpen] = useState(false);

  /*Redux Data */
  const lastResponse = useSelector((state) => state.file.lastResponse);
  const planUploadLimit = useSelector((state) => state.auth.planUploadLimit);

  const plan_upload_limit = planUploadLimit || (lastResponse?.plan_upload_limit ?? 0);
  const user_upload_count = lastResponse?.user_upload_count ?? 0;

  const remaining_uploads = Math.max(
    plan_upload_limit - user_upload_count,
    0
  );

  const usage_percentage =
    plan_upload_limit > 0 ? (user_upload_count / plan_upload_limit) * 100 : 0;

  const get_usage_color = () => {
    if (usage_percentage >= 95) {
      return "from-red-500 to-red-600"; // Critical
    }

    if (usage_percentage >= 80) {
      return "from-orange-400 to-orange-500"; // Warning
    }

    return "from-purple-500 to-blue-500"; // Normal
  };

  useEffect(() => {
  if (!lastResponse) return;

  if (usage_percentage >= 95 && !is_subscription_modal_open) {
    setIsSubscriptionModalOpen(true);
  }
}, [usage_percentage, lastResponse]);

    

  /*Active Menu Logic */
  const selectedKey = useMemo(() => {
    if (location.pathname.startsWith("/dashboard/data-entry"))
      return "data-entry";
    if (location.pathname.startsWith("/dashboard/smart-merge"))
      return "smart-merge";
    if (location.pathname.startsWith("/dashboard/anamoly-detection"))
      return "anamoly-detection";
    if (location.pathname.startsWith("/dashboard/data-history"))
      return "data-history";
    if (location.pathname.startsWith("/dashboard/use-case"))
      return "use-case";
    return "home";
  }, [location.pathname]);

  const menuItems = getMenuItems(navigate, handleLogout);

  return (
    <>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        collapsedWidth={84}
        breakpoint="lg"
        onBreakpoint={(broken) => setCollapsed(broken)}
        className="bg-[#1C1C1E] h-full border-r border-[#3A3A3C] !flex !flex-col"
        width={240}
      >
        {/* Header */}
        <div className="h-24 flex items-center justify-center text-white text-lg font-semibold tracking-wide shrink-0">
          <div className="flex items-center gap-2">
            {!collapsed && (
              <span className="inline-block rounded-md m-4">
                <img
                  src={logo}
                  alt="XynexAi Logo"
                  className="w-32 h-auto object-contain"
                />
              </span>
            )}
          </div>

          <button
            type="button"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            onClick={() => setCollapsed(!collapsed)}
            className="inline-flex items-center justify-center w-14 h-9 rounded-md transition hover:bg-white/10"
          >
            {collapsed ? (
              <img
                src={small_logo}
                alt="XynexAI small logo"
                className="w-10 h-10 object-contain"
              />
            ) : (
              <MenuFoldOutlined className="text-white text-xl" />
            )}
          </button>
        </div>

        {/* Menu + Bottom Section */}
        <div className="h-[88vh] flex flex-col">
          <Menu
            mode="inline"
            theme="dark"
            selectedKeys={[selectedKey]}
            items={menuItems}
            className="!bg-[#1C1C1E] text-base font-medium border-t border-slate-600 flex-1"
          />

          {/* Usage / Upgrade Card */}
          {!collapsed && (
            <div className="mx-3 mb-3 rounded-xl bg-[#2C2C2E] border border-[#3A3A3C] p-4 text-white overflow-hidden">
              {lastResponse || planUploadLimit ? (
                <>
                  <div className="flex items-center justify-between mb-1">
                    <div
                      className={`text-sm font-semibold ${
                        usage_percentage >= 95
                          ? "text-red-400"
                          : usage_percentage >= 80
                            ? "text-orange-300"
                            : "text-white"
                      }`}
                    >
                      {lastResponse
                      ? `${remaining_uploads} uploads left`
                      : `Total uploads limit : ${plan_upload_limit}`}
                    </div>
                  </div>

                  <div className="text-xs text-gray-400 mb-3">
                    Upgrade for unlimited usage
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full h-1 bg-[#3A3A3C] rounded-full overflow-hidden mb-4">
                    <div
                      className={`h-full rounded-full bg-gradient-to-r ${get_usage_color()} transition-all duration-300`}
                      style={{ width: `${usage_percentage}%` }}
                    />
                  </div>
                </>
              ) : (
                /* Loading Skeleton */
                <>
                  <div className="h-4 w-28 bg-[#3A3A3C] rounded mb-2 animate-pulse" />
                  <div className="h-3 w-40 bg-[#3A3A3C] rounded mb-4 animate-pulse" />
                  <div className="h-1 w-full bg-[#3A3A3C] rounded animate-pulse mb-4" />
                </>
              )}

              {/* Upgrade Button */}
              <button
                onClick={() => navigate("/dashboard/subscription")}
                className="w-full py-2.5 text-sm font-semibold rounded-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 active:scale-[0.985] transition-all duration-200 flex items-center justify-center gap-2 shadow-lg"
              >
                <span>🚀</span>
                <span>Upgrade Plan</span>
              </button>
            </div>
          )}

          {collapsed && (
            <div className="flex justify-center mb-3">
              <button
                onClick={() => navigate("/dashboard/subscription")}
                className="w-10 h-10 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg"
                title={
                  lastResponse || planUploadLimit
                    ? `${remaining_uploads} uploads left`
                    : "Loading usage..."
                }
              >
                🚀
              </button>
            </div>
          )}

          {/* ✅ Profile Section */}
          <div>
            <ProfileSection
              collapsed={collapsed}
              userInfo={userInfo}
              handleLogout={handleLogout}
            />
          </div>
        </div>
      </Sider>
      <SubscriptionModal
        open={is_subscription_modal_open}
        onClose={() => setIsSubscriptionModalOpen(false)}
        navigate={navigate}
      />
    </>
  );
};

export default Sidebar;